import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';

interface Country {
    CountryName: string;
}

interface ComponentProps {
    countryId: number;
    countryName: string;
    handleClose: () => void;
}

const UpdateCountry: React.FC<ComponentProps> = ({ handleClose, countryName, countryId }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const updateCountry = (data: Country) => {
        return apiCall().put(`/Country/updateCountry/${countryId}`, data);
    };

    const updateCountryMutation = useMutation(['update-country'], updateCountry, {retry: 0});

    const updateCountryHandler = async (values: FormikValues) => {
        const payload: Country = {
            CountryName: values?.name,
        };

        try {
            await updateCountryMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Country successfully updated.',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-countries']);
                    handleClose();
                }
            })
        }catch(error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: countryName ? countryName : ''
        },
        onSubmit: (values) => {
            updateCountryHandler(values);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Country Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateCountryMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={updateCountryMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateCountry;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

